import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { OutletProvider } from "./components/OutletContext";
import { ProtectedRoute, PublicOnlyRoute, PlanGate, OwnerOnlyRoute } from "./components/RouteGuards";
import AppShell from "./components/AppShell";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import SetPasswordPage from "./pages/SetPasswordPage";
import WelcomePage from "./pages/WelcomePage";
import DashboardPage from "./pages/DashboardPage";
import IngredientsPage from "./pages/IngredientsPage";
import RecipesPage from "./pages/RecipesPage";
import CocktailsPage from "./pages/CocktailsPage";
import AIGeneratorPage from "./pages/AIGeneratorPage";
import PlatingIntelligencePage from "./pages/PlatingIntelligencePage";
import DrinkIntelligencePage from "./pages/DrinkIntelligencePage";
import AdminPage from "./pages/AdminPage";
import AboBillingPage from "./pages/AboBillingPage";
import BillingSuccessPage from "./pages/BillingSuccessPage";
import SettingsPage from "./pages/SettingsPage";
import AllergensPage from "./pages/AllergensPage";
import MenusPage from "./pages/MenusPage";
import WastePage from "./pages/WastePage";
import EngineeringPage from "./pages/EngineeringPage";
import HotelPage from "./pages/HotelPage";
import ReportsPage from "./pages/ReportsPage";
import InvoicesPage from "./pages/InvoicesPage";
import EventMenusPage from "./pages/EventMenusPage";
import AffiliatePage from "./pages/AffiliatePage";
import OutletsPage from "./pages/OutletsPage";
import UnifiedDashboardPage from "./pages/UnifiedDashboardPage";
import PricingPage from "./pages/PricingPage";
import ImpressumPage from "./pages/legal/ImpressumPage";
import DatenschutzPage from "./pages/legal/DatenschutzPage";
import AGBPage from "./pages/legal/AGBPage";
import CookieConsent from "./components/CookieConsent";
import { Spinner } from "./components/ui/Primitives";

const MARKETING_SITE = "https://studioops.io";

function ExternalRedirect({ to }) {
  React.useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-void">
      <Spinner size={28} />
    </div>
  );
}

function RootRoute() {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-void">
        <Spinner size={28} />
      </div>
    );
  }
  if (user && user !== false) {
    return <Navigate to={user.role === "super_admin" ? "/admin" : "/dashboard"} replace />;
  }
  return <Navigate to="/login" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <OutletProvider>
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#161B22",
            color: "#E6EDF3",
            border: "1px solid rgba(255,255,255,0.1)",
            fontSize: "13px",
            borderRadius: "8px",
          },
        }}
      />
      <Routes>
        {/* Public root = redirect to login (landing page is on studioops.io) */}
        <Route path="/" element={<RootRoute />} />

        {/* Public auth */}
        <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>} />
        <Route path="/reset-password" element={<PublicOnlyRoute><ResetPasswordPage /></PublicOnlyRoute>} />
        <Route path="/set-password" element={<PublicOnlyRoute><SetPasswordPage /></PublicOnlyRoute>} />

        {/* Public post-checkout */}
        <Route path="/welcome" element={<WelcomePage />} />

        {/* Public affiliate program — moved to the marketing site */}
        <Route path="/partner" element={<ExternalRedirect to={`${MARKETING_SITE}/partner`} />} />

        {/* Public legal pages (DSGVO) */}
        <Route path="/impressum" element={<ImpressumPage />} />
        <Route path="/datenschutz" element={<DatenschutzPage />} />
        <Route path="/agb" element={<AGBPage />} />

        {/* Protected */}
        <Route path="/billing/success" element={<ProtectedRoute><BillingSuccessPage /></ProtectedRoute>} />
        <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/ingredients" element={<IngredientsPage />} />
          <Route path="/recipes" element={<PlanGate module="kitchen"><RecipesPage /></PlanGate>} />
          <Route path="/cocktails" element={<PlanGate module="bar"><CocktailsPage /></PlanGate>} />
          <Route path="/ai" element={<PlanGate module="ai"><AIGeneratorPage /></PlanGate>} />
          <Route path="/plating" element={<PlatingIntelligencePage />} />
          <Route path="/drink" element={<DrinkIntelligencePage />} />
          <Route path="/allergens" element={<PlanGate module="allergens"><AllergensPage /></PlanGate>} />
          <Route path="/menus" element={<PlanGate module="menus"><MenusPage /></PlanGate>} />
          <Route path="/event-menus" element={<PlanGate module="menus"><EventMenusPage /></PlanGate>} />
          <Route path="/waste" element={<PlanGate module="waste"><WastePage /></PlanGate>} />
          <Route path="/engineering" element={<PlanGate module="engineering"><EngineeringPage /></PlanGate>} />
          <Route path="/hotel" element={<PlanGate module="hotel"><HotelPage /></PlanGate>} />
          <Route path="/reports" element={<PlanGate module="reports"><ReportsPage /></PlanGate>} />
          <Route path="/invoices" element={<PlanGate module="invoices"><InvoicesPage /></PlanGate>} />
          <Route path="/affiliate" element={<AffiliatePage />} />
          <Route path="/outlets" element={<OutletsPage />} />
          <Route path="/outlets/dashboard" element={<OwnerOnlyRoute><UnifiedDashboardPage /></OwnerOnlyRoute>} />
          <Route path="/billing" element={<OwnerOnlyRoute><AboBillingPage /></OwnerOnlyRoute>} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
        </Route>

        {/* /register is removed — redirect any old bookmarks to the pricing/landing */}
        <Route path="/register" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <CookieConsent />
      </OutletProvider>
    </AuthProvider>
  );
}
